import React from 'react';
import { Link } from 'react-router-dom';
import { Battery, Zap, Clock, Shield, ArrowRight, Sun, Leaf, Wrench } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';
import { useSection } from '../../hooks/useSection';

const ICONS: Record<string, React.ComponentType<any>> = { Battery, Zap, Clock, Shield, Sun, Leaf, Wrench };

const DEFAULT_FEATURES = [
  { icon: 'Battery', title: 'Recharge rapide', description: 'Rechargez votre moto en 2-3 heures seulement avec nos bornes de recharge rapide.' },
  { icon: 'Zap', title: 'Énergie solaire', description: 'Stations alimentées par énergie solaire — recharge 100% verte.' },
  { icon: 'Clock', title: '24/7 disponible', description: 'Accès aux stations jour et nuit, avec assistance téléphonique permanente.' },
  { icon: 'Shield', title: '100% sécurisé', description: 'Paiement sécurisé et surveillance vidéo pour votre tranquillité.' },
];
const DEFAULT_STATS = [
  { value: '4+', label: 'Stations actives' },
  { value: '100%', label: 'Énergie solaire' },
  { value: '24/7', label: 'Disponibilité' },
];

const ProxyPowerSection: React.FC = () => {
  const { section } = useSection('home_proxypower');
  if (section && section.is_visible === false) return null;
  const ed = section?.extra_data || {};
  const eyebrow = section?.subtitle || 'Proxy Power';
  const intro = ed.intro || 'Notre réseau Proxy Power — recharge rapide, fiable et 100% écologique.';
  const body = ed.body || "Notre technologie de recharge rapide réduit votre temps d'arrêt au minimum. L'application Proxy localise la station la plus proche et suit votre consommation en temps réel.";
  const stats = (ed.stats?.length ? ed.stats : DEFAULT_STATS) as Array<{ value: string; label: string }>;
  const features = (ed.features?.length ? ed.features : DEFAULT_FEATURES) as Array<{ icon?: string; title: string; description: string }>;
  const btnLabel = section?.button_label || 'Trouver une station';
  const btnUrl = section?.button_url || '/stations';

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-neutral-50/60 to-white py-14 sm:py-20 lg:py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 right-[10%] h-96 w-96 rounded-full bg-primary-500/[0.05] blur-3xl" />
        <div className="absolute bottom-0 left-[8%] h-[28rem] w-[28rem] rounded-full bg-neutral-900/[0.03] blur-3xl" />
      </div>
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-2xl text-center sm:mb-16">
          <SectionLabel number="07" className="mb-4">
            {eyebrow}
          </SectionLabel>
          <h2 className="mb-4 text-3xl font-black leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-4xl lg:text-5xl">
            {section?.title ? section.title : (<>Stations de recharge <span className="text-primary-600">solaires</span></>)}
          </h2>
          <p className="mx-auto max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg">
            {intro}
          </p>
        </div>

        <div className="mb-12 grid grid-cols-1 items-center gap-6 lg:mb-16 lg:grid-cols-2 lg:gap-8">
          <div>
            <div className="mb-6 grid grid-cols-3 gap-3 sm:gap-4">
              {stats.map((stat, r) => (
                <div
                  key={r}
                  className="rounded-2xl bg-white p-4 sm:p-5 ring-1 ring-neutral-200/80 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_rgba(0,0,0,0.12)]"
                >
                  <div className="text-3xl font-black tracking-tight text-neutral-900 sm:text-4xl">{stat.value}</div>
                  <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-neutral-500 sm:text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
            <p className="mb-6 text-base leading-relaxed text-neutral-600 sm:text-lg">
              {body}
            </p>
            <Link
              to={btnUrl}
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-neutral-900 px-6 py-3.5 text-sm font-bold text-white shadow-[0_14px_30px_-12px_rgba(0,0,0,0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-600 hover:shadow-[0_18px_40px_-12px_rgba(24,153,78,0.55)]"
            >
              {btnLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="group relative overflow-hidden rounded-3xl bg-white p-6 ring-1 ring-neutral-200/80 shadow-[0_12px_40px_-15px_rgba(0,0,0,0.12)] transition-all duration-500 hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.18)] sm:p-8">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-primary-500/[0.06] blur-3xl" />
            </div>
            <div className="relative flex items-center justify-center">
              <img
                src="/Station_Proxy_Power.webp"
                alt="Station Proxy Power solaire — Matam, Sénégal"
                className="h-auto max-w-full rounded-2xl transition-transform duration-700 group-hover:scale-[1.02]"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feat, r) => {
            const Icon = (feat.icon && ICONS[feat.icon]) || Battery;
            return (
              <div
                key={r}
                className="group rounded-3xl bg-white p-6 ring-1 ring-neutral-200/80 shadow-[0_8px_30px_-15px_rgba(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_40px_-15px_rgba(0,0,0,0.14)]"
              >
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 ring-1 ring-primary-100 transition-transform duration-500 group-hover:scale-110">
                  <Icon className="h-5 w-5 text-primary-600" strokeWidth={2.5} />
                </div>
                <h3 className="mb-2 text-base font-black tracking-tight text-neutral-900 sm:text-lg">{feat.title}</h3>
                <p className="text-sm leading-relaxed text-neutral-600">{feat.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProxyPowerSection;
